import { handleError } from 'magic/handlers';
import { Nullable } from 'types/_common';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { fetchPermissionList, PermissionInListDTO } from 'services/permission';

type UsePermissionListParams = {
  userId?: Nullable<number>;
};

type UsePermissionListResult = {
  isFetchingPermissionList: boolean;
  hasPermission: (sysNamesToCheck: string[]) => boolean;
  hasPermissionForOrgs: (sysNamesToCheck: string[], orgIds: number[]) => boolean;
  getOrgIdsFromPermissions: (sysNames: string[]) => number[];
};

export const usePermissionList = ({ userId }: UsePermissionListParams): UsePermissionListResult => {
  // @ts-ignore
  const userProfilePermissionList = useSelector((state) => state.core.session.userProfilePermissionList) as PermissionInListDTO[];

  const [isFetchingPermissionList, setIsFetchingPermissionList] = useState(true);
  const [permissionList, setPermissionList] = useState<PermissionInListDTO[]>([]);

  const hasPermission = (sysNamesToCheck: string[]) => {
    const list = typeof userId !== 'number' ? userProfilePermissionList : permissionList;
    if (sysNamesToCheck.length === 0) {
      return false;
    }
    if (sysNamesToCheck.length === 1) {
      const sysNameToCheck = sysNamesToCheck[0];
      return list.some((d) => d.permission?.sysName === sysNameToCheck);
    }
    return list.some((d) => sysNamesToCheck.some((s) => s === d.permission?.sysName));
  };

  const getOrgIdsFromPermissions = useCallback(
    (sysNames: string[]) => {
      return (typeof userId !== 'number' ? userProfilePermissionList : permissionList)
        .filter((d) => {
          const sysName = d.permission?.sysName;
          return sysName != null ? sysNames.includes(sysName) : false;
        })
        .map((d) => d.organizationId)
        .filter((d) => typeof d === 'number');
    },
    [userId, userProfilePermissionList, permissionList],
  );

  const hasPermissionForOrgs = (sysNamesToCheck: string[], orgIds: Nullable<number>[]) => {
    const orgIdsFromPermissions = getOrgIdsFromPermissions(sysNamesToCheck);
    return orgIdsFromPermissions.some((orgId) => orgIds.includes(orgId));
  };

  useEffect(() => {
    (async () => {
      if (typeof userId !== 'number') {
        setIsFetchingPermissionList(false);
        return;
      }
      try {
        const { data } = await fetchPermissionList({ personId: userId });
        setPermissionList(data?.permissions ?? []);
      } catch (error) {
        handleError(error as object);
      } finally {
        setIsFetchingPermissionList(false);
      }
    })();
  }, []);

  return { isFetchingPermissionList, hasPermission, hasPermissionForOrgs, getOrgIdsFromPermissions };
};
